  import { defineComponent, type PropType } from "vue";
  import AuthorizedCreditCreditLimit from '@/extend/controls/authorizedCredit/AuthorizedCreditCreditLimit';
  import AuthorizedCreditCreditLimitUntil from '@/extend/controls/authorizedCredit/AuthorizedCreditCreditLimitUntil';
  import { Period } from '@/framework/base/helpers/DateFormat';
  import AuthorizedCreditPermanent from '@/extend/controls/authorizedCredit/AuthorizedCreditPermanent';
  import EtablissementSelect from '@/extend/controls/etablissement/EtablissementSelect';
  import AuthorizedCredit from '@/extend/classes/AuthorizedCredit';
  import { FieldStatus, EditFieldTheme, SectionStatus } from '@/framework/base/components/FieldInfo';
  import { Alerts } from '@/framework/base/helpers/Alerts'; 
  import { storeAuthorizedCredit } from '@/generated/stores';
  import LoaderButton from '@/framework/base/components/LoaderButton.vue';
  import SectionHeader from '@/framework/base/components/SectionHeader.vue';
  import { Extended } from '@/framework/base/classes/Schema';
  import ButtonPlus from '@/framework/base/components/ButtonPlus.vue';
  import Route from '@/generated/classes/Route';
  import { Fiches, Sections } from '@/generated/classes/AppSchema';
  import { ObjTypes } from '@/generated/classes/ObjTypes';
  export default defineComponent({
    components: {
      SectionHeader,
      LoaderButton,
      ButtonPlus,
      AuthorizedCreditCreditLimit,
      AuthorizedCreditCreditLimitUntil,
      AuthorizedCreditPermanent,
      EtablissementSelect,
    },

    props: {
      authorizedCredit: {
        type: Object as PropType<AuthorizedCredit>,
        required: true
      },
      showButton: {
        type: Boolean,
      },
      autoSave: {
        type: Boolean,
      },
      fieldOnly: {
        type: Boolean,
      },
      extended: {
        type: Boolean,
      },
      autoClear: {
        type: Boolean,
      },
      dialog: {
        type: Boolean,
      },
      closeSection: {
        type: Boolean,
      },
      showMissing: {
        type: Boolean,
      },
      moduleId: {
        type: Number,
      },
      theme: {
        type: Object as PropType<EditFieldTheme>,
      },
      fiche: {
        type: String,
      },
      fromSection: {
        type: String,
      },
      requiredAttributs: {
        type: Array as PropType<string[]>,
      },
      toggleResetEtablissementSelect: {
        type: Boolean
      },
    },

    beforeUnmount (): void {
      if (!this.closeSection) {
        this.beforeClose();
      }
    },

    data() {
      return {
        creditLimitStatus: new FieldStatus(false, false, 0),
        strCreditLimit: '',
        creditLimitUntilStatus: new FieldStatus(false, false, 0),
        strCreditLimitUntil: '',
        permanentStatus: new FieldStatus(false, false, 0),
        strPermanent: '',
    toggleEtablissementId: false,
        etablissementIdStatus: new FieldStatus(false, false, 0),
        strEtablissementId: '',
        refAuthorizedCredit: new AuthorizedCredit,
        cloneAuthorizedCredit: new AuthorizedCredit,
      authorizedCreditSaving: false,
        saving: false,
        restoring: false,
        inDialog: false,
        updated: false,
        error: false,
        saveLoading: false,
        saveFailed: false,
        allowClearRef: true,
        objType: ObjTypes.AUTHORIZED_CREDIT,
        status: new SectionStatus(false, false, false),
        autoClose: true,
        closing: false
      }
    },
    computed: {
      isExtended (): boolean {
        return this.extended;
      },

      exist (): boolean {
        return this.editAuthorizedCredit.id != 0
      },

      extendIcon (): string {
        return this.extended ? 'fas fa-circle-minus' : 'fas fa-circle-plus';
      },

      saveButton (): boolean {
        return (this.showButton == undefined || this.showButton == true) && !this.allReadOnly;
      },

      editAuthorizedCredit: {
        get(): AuthorizedCredit {
          if (this.closeSection) {
            this.beforeClose();
          } else {
            if (this.authorizedCredit != undefined) {
              if (this.authorizedCredit.id == 0) {
                Alerts.debug('CrudAuthorizedCredit : les objets passés en paramètre doivent être issus du store (id <> 0)');
              }
              if (this.cloneAuthorizedCredit.id != this.authorizedCredit.id && !this.saving) {
                this.handleAuthorizedCreditChange();
              }
            }
          }
          return this.refAuthorizedCredit;
        },
        set(value: AuthorizedCredit) {
          this.refAuthorizedCredit = value;
        }
      },

      canCreate (): boolean {
        return this.refAuthorizedCredit.id > 0;
      },

      canDelete (): boolean {
        return true;
      },

      updatedAndValid (): boolean {
        return this.updated && !this.error;
      },

      clearAuto (): boolean {
        return (this.autoClear == undefined || this.autoClear == true) && this.allowClearRef;
      },


      creditLimit: {
        get(): string {
          return this.strCreditLimit;
        },
        set(value: string) {
          const creditLimit = parseFloat(value);
          if (creditLimit.toString() == value) {
            this.editAuthorizedCredit.creditLimit = creditLimit;
            this.fieldUpdated('CREDIT_LIMIT', value);
          }
          this.strCreditLimit = value;
        }
      },


      creditLimitUntil: {
        get(): Date | null {
          return this.editAuthorizedCredit.creditLimitUntil;
        },
        set(value: Date | null) {
          this.editAuthorizedCredit.creditLimitUntil = value;
          this.fieldUpdated('CREDIT_LIMIT_UNTIL', value);
        }
      },

      periodCreditLimitUntil() : Period[] {
        return []
      },

      permanent: {
        get(): boolean {
          return this.editAuthorizedCredit.permanent;
        },
        set(value: boolean) {
          this.editAuthorizedCredit.permanent = value;
          this.fieldUpdated('PERMANENT', value);
        }
      },

    resetEtablissementId (): boolean {
      return this.toggleEtablissementId
    },

      etablissementId: {
        get(): number {
          return this.editAuthorizedCredit.etablissementId;
        },
        set(value: number) {
          this.editAuthorizedCredit.etablissementId = value;
          this.fieldUpdated('ETABLISSEMENT_ID', value);
        }
      },


      allReadOnly (): boolean {
        const res = (
        (this.isDisplayed('CREDIT_LIMIT') && !this.readOnly('CREDIT_LIMIT')) ||
        (this.isDisplayed('CREDIT_LIMIT_UNTIL') && !this.readOnly('CREDIT_LIMIT_UNTIL')) ||
        (this.isDisplayed('PERMANENT') && !this.readOnly('PERMANENT')) ||
        (this.isDisplayed('ETABLISSEMENT_ID') && !this.readOnly('ETABLISSEMENT_ID')));
        return !res;
      },

      fields (): string[] {
        return ['CREDIT_LIMIT', 'CREDIT_LIMIT_UNTIL', 'PERMANENT', 'ETABLISSEMENT_ID'];
      },
    },

    methods: {
      handleAuthorizedCreditChange() : void {
        // Edited object changed
        this.saveFailed = false;
        if (this.autoSave && this.updatedAndValid) {
          if (!this.inDialog && this.checkFields()) {
            // Dialogue to save or cancel changes
            this.inDialog = true;
            this.dialogSaveOrCancel().then(() => {
              this.createRef(this.authorizedCredit);
              this.inDialog = false;
                  })
          } else if (!this.restoring && !this.inDialog) {
            this.restoreEdited();
            this.createRef(this.authorizedCredit);
          }
        } else if (!this.inDialog) {
          if (this.cloneAuthorizedCredit.id < 0) {
            this.clearRef();
          } else if (this.updated) {
            this.restoreEdited();
          }
          this.createRef(this.authorizedCredit);
        }
      },
      beforeClose (): void {
        if (!this.closing) {
          this.closing = true;
          if (this.autoSave && this.updatedAndValid && this.checkFields()) {
            this.$emit('saveOrCancel');
            this.dialogSaveOrCancel();
          } else {
            if (this.autoSave && this.updated) {
              this.restoreEdited();
            }
            this.$emit('cancelled');
          }
          this.clearRef();
          }
      },

      setExtended (extended: Extended): void {
         this.$emit('extended', extended);
      },

      // eslint-disable-next-line
      isDisplayed (value: string): boolean {
        return true;
      },

      // eslint-disable-next-line
      required (value: string): boolean {
        return false;
      },

      // eslint-disable-next-line
      readOnly (value: string): boolean {
        return false;
      },

      save (): void {
        if (event != undefined) {
          event.stopPropagation();
        }
        this.saveLoading = true;
        this.saveFailed = false;
        if (this.checkFields()) {
          this.saveObj();
        } else {
        this.saveLoading = false;
          this.saveFailed = true;
        }
      },

      setUpdated (): void {
        const updated = (this.creditLimitStatus != undefined && this.creditLimitStatus.updated) ||
          (this.creditLimitUntilStatus != undefined && this.creditLimitUntilStatus.updated) ||
          (this.permanentStatus != undefined && this.permanentStatus.updated) ||
          (this.etablissementIdStatus != undefined && this.etablissementIdStatus.updated);
        if (updated != this.updated) {
          this.updated = updated;
          this.status.updated = updated;
          this.status.missing = this.hasMissing();
          if (!this.closing) {
            this.$emit('status', this.status);
          }
          this.$emit('updated', this.authorizedCredit);
        }
      },

      setError (): void {
        const error = this.creditLimitStatus.error ||
        this.creditLimitUntilStatus.error ||
        this.permanentStatus.error ||
        this.etablissementIdStatus.error;
        if (error != this.error) {
          if (!error) {
            this.saveFailed = false;
          }
          this.error = error;
          this.status.error = error;
          if (!this.closing) {
            this.$emit('status', this.status);
          }
          this.$emit('updated', this.authorizedCredit);
        }
      },

      // Clear non saved new objects when leaving
      clearRef (): void {
        if (this.clearAuto && this.refAuthorizedCredit.id < 0) {
          storeAuthorizedCredit.mAuthorizedCreditDeleted(this.refAuthorizedCredit.id);
        }
      },

      // Dialog to save or ignore changes when loosing focus or changing edited item
      dialogSaveOrCancel (): Promise<boolean> {
        return new Promise((resolve) => {
          if (!this.error) {
            Alerts.saveOrCancel()
              .then((result: boolean) => {
                if (result) {
                  this.saveObj();
                } else {
                  if (this.refAuthorizedCredit.id <= 0) {
                    this.deleteObj();
                  } else {
                    this.restoreEdited();
                  }
                  this.$emit('cancelled');
                }
                resolve(result);
              })
          } else {
            this.restoreEdited();
          }
        })
      },

      // Save object and subobjects referenced by foreignClass keys
      saveObj (): void {
        this.saving = true;
        if (
          this.saveAuthorizedCredit()
          ) {
          this.saving = false;
          this.saveLoading = false;
          this.authorizedCreditSaving = false;
          this.createRef(this.refAuthorizedCredit);
          this.$emit('updated', this.refAuthorizedCredit);
          if (this.authorizedCredit.id > 0) {
            this.$emit('saved', this.refAuthorizedCredit);
          } else {
            this.updated = true;
          }
          if (this.autoClose) {
            this.saveFields();
            this.$emit('closeDialog');
          }
        } else {
          if (this.refAuthorizedCredit.failed) {
          this.saveLoading = false;
            this.authorizedCreditSaving = false;
            this.refAuthorizedCredit.failed = false;
          }
        }
      },

      saveAuthorizedCredit (): boolean {
        if (!this.refAuthorizedCredit.compareAuthorizedCredit(this.cloneAuthorizedCredit)) {
          if (!this.authorizedCreditSaving) {
            this.authorizedCreditSaving = true;
          }
          return false;
        } else {
          return true;
        }
      },

      // Delete object
      deleteObj (): void {
        if (this.refAuthorizedCredit.id <= 0) {
          storeAuthorizedCredit.mAuthorizedCreditDeleted(this.refAuthorizedCredit.id);
        }
      },

      statusCreditLimit (value: FieldStatus): void {
        this.creditLimitStatus = value;
        this.setUpdated();
        this.setError();
      },
      statusCreditLimitUntil (value: FieldStatus): void {
        this.creditLimitUntilStatus = value;
        this.setUpdated();
        this.setError();
      },
      statusPermanent (value: FieldStatus): void {
        this.permanentStatus = value;
        this.setUpdated();
        this.setError();
      },
      statusEtablissementId (value: FieldStatus): void {
        this.etablissementIdStatus = value;
        this.setUpdated();
        this.setError();
      },

      deleteAuthorizedCredit(): void {
        this.$emit('deleteAuthorizedCredit',  this.authorizedCredit);
      },

    addAuthorizedCredit(): void {
      this.$emit('addAuthorizedCredit',  this.authorizedCredit);
    },

      // Restore object
      restoreEdited (): void {
        if (this.cloneAuthorizedCredit != undefined) {
          this.restoring = true;
          this.refAuthorizedCredit.creditLimit = this.cloneAuthorizedCredit.creditLimit;
          this.refAuthorizedCredit.creditLimitUntil = this.cloneAuthorizedCredit.creditLimitUntil;
          this.refAuthorizedCredit.permanent = this.cloneAuthorizedCredit.permanent;
          this.refAuthorizedCredit.etablissementId = this.cloneAuthorizedCredit.etablissementId;
          this.restoring = false;
        }
      },


    // Clone and clear edited
      createRef (value: AuthorizedCredit): void {
        this.clearRef();
        this.editAuthorizedCredit = value;
        this.cloneAuthorizedCredit = value.cloneAuthorizedCredit();
        this.creditLimitStatus.clear(value.id);
        this.creditLimitUntilStatus.clear(value.id);
        this.permanentStatus.clear(value.id);
        this.etablissementIdStatus.clear(value.id);
        this.status.clear();
        this.strCreditLimit = this.editAuthorizedCredit.creditLimit.toString();
      },

    checkFields (): boolean {
        // @ts-expect-error : generated method
        if (this.isDisplayed('CREDIT_LIMIT') && this.$refs.CREDIT_LIMIT) this.$refs.CREDIT_LIMIT.touch();
        // @ts-expect-error : generated method
        if (this.isDisplayed('CREDIT_LIMIT_UNTIL') && this.$refs.CREDIT_LIMIT_UNTIL) this.$refs.CREDIT_LIMIT_UNTIL.touch();
        // @ts-expect-error : generated method
        if (this.isDisplayed('PERMANENT') && this.$refs.PERMANENT) this.$refs.PERMANENT.touch();
        // @ts-expect-error : generated method
        if (this.isDisplayed('ETABLISSEMENT_ID') && this.$refs.ETABLISSEMENT_ID) this.$refs.ETABLISSEMENT_ID.touch();
        return !this.error;
      },

      missing (value: string): boolean {
        return this.hasMissing(value);
      },

      hasMissing (value = ''): boolean {
        let missing = false;
        // @ts-expect-error : generated method
        if ((value == '' || value == 'CREDIT_LIMIT') && this.isDisplayed('CREDIT_LIMIT') && this.$refs.CREDIT_LIMIT && this.$refs.CREDIT_LIMIT.missing()) missing = true;
        // @ts-expect-error : generated method
        if ((value == '' || value == 'CREDIT_LIMIT_UNTIL') && this.isDisplayed('CREDIT_LIMIT_UNTIL') && this.$refs.CREDIT_LIMIT_UNTIL && this.$refs.CREDIT_LIMIT_UNTIL.missing()) missing = true;
        // @ts-expect-error : generated method
        if ((value == '' || value == 'PERMANENT') && this.isDisplayed('PERMANENT') && this.$refs.PERMANENT && this.$refs.PERMANENT.missing()) missing = true;
        // @ts-expect-error : generated method
        if ((value == '' || value == 'ETABLISSEMENT_ID') && this.isDisplayed('ETABLISSEMENT_ID') && this.$refs.ETABLISSEMENT_ID && this.$refs.ETABLISSEMENT_ID.missing()) missing = true;
        return missing;
      },

      saveFields (): void {
        // @ts-expect-error : generated method
        if (this.isDisplayed('CREDIT_LIMIT') && this.$refs.CREDIT_LIMIT) this.$refs.CREDIT_LIMIT.save();
        // @ts-expect-error : generated method
        if (this.isDisplayed('CREDIT_LIMIT_UNTIL') && this.$refs.CREDIT_LIMIT_UNTIL) this.$refs.CREDIT_LIMIT_UNTIL.save();
        // @ts-expect-error : generated method
        if (this.isDisplayed('PERMANENT') && this.$refs.PERMANENT) this.$refs.PERMANENT.save();
        // @ts-expect-error : generated method
        if (this.isDisplayed('ETABLISSEMENT_ID') && this.$refs.ETABLISSEMENT_ID) this.$refs.ETABLISSEMENT_ID.save();
      },

      getValue (field: string): string {
         if (field == 'CREDIT_LIMIT') return this.creditLimit.toString();
        else  if (field == 'CREDIT_LIMIT_UNTIL') return this.creditLimitUntil == null ? '' : this.creditLimitUntil.toString();
        else  if (field == 'PERMANENT') return this.permanent.toString();
        else  if (field == 'ETABLISSEMENT_ID') return this.etablissementId.toString();
        else return '';
      },

    // eslint-disable-next-line
    fieldUpdated (field: string, value: unknown): void {
      // To implement
    },

      setFocus (route: Route): boolean {
        let res = false;
        let first = '';
        const attribut = route ? route.attribut : '';
        this.fields.forEach(field => {
          // @ts-expect-error : generated method
          if (!res && this.isDisplayed(field) && !this.readOnly(field) && this.$refs[field].setFocus) {
            if (attribut == field) {
              // @ts-expect-error : generated method
              this.$refs[attribut].setFocus();
              res = true;
            } else if (first == '') {
              first = field;
            }
          }
        })
        // @ts-expect-error : generated method
        if (!res && first != '' && this.$refs[first] && this.$refs[first].setFocus) {
          // @ts-expect-error : generated method
          this.$refs[first].setFocus();
          res = true;
        }
        return res
      },

      closeDialog(): void {
        this.$emit('closeDialog')
      }
    },
  })
